import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./EnvironmentMap.scss";
import Label from "./Label";
import ContentAPI from "../services/ContentAPI";
import {
  getSystemEnvironmentAvailabilityById,
  getSystemEnvironments,
} from "../utils/systemData";

const THUMBNAIL_CACHE_NAME = "environment-thumbnail-cache-v1";
const thumbnailUrlMemoryCache = new Map();
const CUSTOM_LAYOUT_WIDTH = 420;
const CUSTOM_LAYOUT_HEIGHT = 236;
const MIN_CUSTOM_LAYOUT_WIDTH = 180;
const MAX_CUSTOM_LAYOUT_WIDTH = 1200;
const MIN_CUSTOM_LAYOUT_HEIGHT = 120;
const MAX_CUSTOM_LAYOUT_HEIGHT = 900;
const CURRENT_NODE_DIAMETER = 50;
const COLLAPSED_MAP_HEIGHT = CURRENT_NODE_DIAMETER + 8;
const LOCKED_NODE_PROMPT_DURATION_MS = 1800;
const LOCKED_NODE_PROMPT_MARGIN = 12;
const LOCKED_NODE_PROMPT_HALF_WIDTH = 92;

const getThumbnailCacheRequestUrl = (environmentId) => {
  if (
    environmentId === null ||
    environmentId === undefined ||
    environmentId === "" ||
    typeof window === "undefined"
  ) {
    return null;
  }

  return new URL(
    `/__environment-thumbnail-cache__/${encodeURIComponent(environmentId)}`,
    window.location.origin,
  ).toString();
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getLockedPromptPosition = (nodeRect) => {
  if (!nodeRect || typeof window === "undefined") {
    return null;
  }

  const viewportWidth = window.innerWidth || 0;
  const viewportHeight = window.innerHeight || 0;
  const centerX = nodeRect.left + nodeRect.width / 2;
  const hasRoomAbove = nodeRect.top > 58;
  const hasRoomBelow = viewportHeight - nodeRect.bottom > 58;
  const placement = hasRoomAbove || !hasRoomBelow ? "above" : "below";
  const x = clamp(
    centerX,
    LOCKED_NODE_PROMPT_MARGIN + LOCKED_NODE_PROMPT_HALF_WIDTH,
    Math.max(
      LOCKED_NODE_PROMPT_MARGIN + LOCKED_NODE_PROMPT_HALF_WIDTH,
      viewportWidth - LOCKED_NODE_PROMPT_MARGIN - LOCKED_NODE_PROMPT_HALF_WIDTH,
    ),
  );
  const y =
    placement === "above"
      ? Math.max(LOCKED_NODE_PROMPT_MARGIN, nodeRect.top - 10)
      : Math.min(viewportHeight - LOCKED_NODE_PROMPT_MARGIN, nodeRect.bottom + 10);

  return { x, y, placement };
};

const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.name === "CanceledError" ||
  error?.code === "ERR_CANCELED";

const getPresignedThumbnailUrl = async (thumbnailId, signal) => {
  if (!thumbnailId) {
    return "";
  }

  const response = await ContentAPI.get(`thumbnails/${thumbnailId}`, {
    signal,
  });
  return typeof response?.data?.url === "string" ? response.data.url.trim() : "";
};

const useCachedThumbnail = (thumbnailId, thumbnailUrl, cacheKey = thumbnailId) => {
  const stableCacheKey = cacheKey || thumbnailId || thumbnailUrl || "";
  const [resolvedThumbnailUrl, setResolvedThumbnailUrl] = useState(
    () => thumbnailUrlMemoryCache.get(stableCacheKey) || thumbnailUrl || "",
  );

  const updateResolvedThumbnailUrl = useCallback(
    (nextUrl, remember = true) => {
      const normalizedUrl = nextUrl ?? "";

      if (remember && stableCacheKey && normalizedUrl) {
        thumbnailUrlMemoryCache.set(stableCacheKey, normalizedUrl);
      }

      setResolvedThumbnailUrl(normalizedUrl);
    },
    [stableCacheKey],
  );

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const loadThumbnail = async () => {
      const cachedMemoryUrl = thumbnailUrlMemoryCache.get(stableCacheKey);
      if (cachedMemoryUrl) {
        updateResolvedThumbnailUrl(cachedMemoryUrl, false);
      }

      if (!thumbnailId && !thumbnailUrl) {
        updateResolvedThumbnailUrl("");
        return;
      }

      const cacheRequestUrl = getThumbnailCacheRequestUrl(stableCacheKey);
      if (
        !cacheRequestUrl ||
        typeof window === "undefined" ||
        typeof window.caches?.open !== "function"
      ) {
        try {
          const freshThumbnailUrl =
            (await getPresignedThumbnailUrl(thumbnailId, abortController.signal)) ||
            thumbnailUrl;

          if (!cancelled) {
            updateResolvedThumbnailUrl(freshThumbnailUrl);
          }
        } catch (error) {
          if (!isAbortError(error) && !cancelled) {
            updateResolvedThumbnailUrl(thumbnailUrl);
          }
        }
        return;
      }

      let cache = null;
      let foundCachedThumbnail = false;
      let sourceUrl = thumbnailUrl;

      try {
        cache = await window.caches.open(THUMBNAIL_CACHE_NAME);
        const cachedResponse = await cache.match(cacheRequestUrl);

        if (cachedResponse && !cancelled) {
          foundCachedThumbnail = true;
          const cachedBlob = await cachedResponse.blob();

          if (cancelled) {
            return;
          }

          updateResolvedThumbnailUrl(URL.createObjectURL(cachedBlob));
        } else if (!cancelled) {
          updateResolvedThumbnailUrl(thumbnailUrl);
        }
      } catch {
        if (!cancelled) {
          updateResolvedThumbnailUrl(thumbnailUrl);
        }
      }

      try {
        sourceUrl =
          (await getPresignedThumbnailUrl(thumbnailId, abortController.signal)) ||
          thumbnailUrl;

        if (!sourceUrl) {
          throw new Error("Thumbnail URL was empty.");
        }

        const response = await fetch(sourceUrl, {
          signal: abortController.signal,
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error(`Unexpected thumbnail status: ${response.status}`);
        }

        if (cache) {
          await cache.put(cacheRequestUrl, response.clone());
        }

        if (cancelled) {
          return;
        }

        const freshBlob = await response.blob();

        if (!cancelled) {
          updateResolvedThumbnailUrl(URL.createObjectURL(freshBlob));
        }
      } catch (error) {
        if (!isAbortError(error) && !foundCachedThumbnail && !cancelled) {
          updateResolvedThumbnailUrl(sourceUrl || thumbnailUrl);
        }
      }
    };

    loadThumbnail();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [stableCacheKey, thumbnailId, thumbnailUrl, updateResolvedThumbnailUrl]);

  return resolvedThumbnailUrl;
};

const parseJsonObject = (value) => {
  if (!value || typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const roundCoordinate = (value) => Number(Math.min(Math.max(value, 0), 1).toFixed(4));

const normalizeInteger = (value, fallback, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.round(Math.min(Math.max(number, min), max));
};

const getEnvironmentElements = (environment) => {
  const sources = [
    environment?.elements,
    environment?.elementJsons,
    environment?.elementDatas,
  ];

  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) return source;
    if (source && typeof source === "object") {
      const values = Object.values(source);
      if (values.length > 0) return values;
    }
  }

  return [];
};

const getEnvironmentId = (environment) =>
  environment?.id || environment?.environmentId || environment?.environmentID || "";

const getTargetEnvironmentId = (element) =>
  element?.targetEnvironmentId ||
  element?.targetEnvironmentID ||
  element?.targetEnvironment?.id ||
  element?.targetEnvironmentDataID ||
  "";

const normalizeWebMapNodes = (webMap) => {
  const parsedWebMap = typeof webMap === "string" ? parseJsonObject(webMap) : webMap;
  const sourceNodes =
    parsedWebMap?.nodes ||
    parsedWebMap?.environments ||
    (parsedWebMap && !parsedWebMap.version ? parsedWebMap : null);

  if (Array.isArray(sourceNodes)) {
    return sourceNodes.reduce((nodes, node) => {
      const environmentId =
        node?.id || node?.environmentId || node?.environmentID || "";
      const x = Number(node?.x);
      const y = Number(node?.y);

      if (environmentId && Number.isFinite(x) && Number.isFinite(y)) {
        nodes[environmentId] = {
          x: roundCoordinate(x),
          y: roundCoordinate(y),
        };
      }

      return nodes;
    }, {});
  }

  if (!sourceNodes || typeof sourceNodes !== "object") {
    return {};
  }

  return Object.entries(sourceNodes).reduce((nodes, [environmentId, node]) => {
    const x = Number(node?.x);
    const y = Number(node?.y);

    if (environmentId && Number.isFinite(x) && Number.isFinite(y)) {
      nodes[environmentId] = {
        x: roundCoordinate(x),
        y: roundCoordinate(y),
      };
    }

    return nodes;
  }, {});
};

const getSystemWebMap = (systemData) => {
  if (systemData?.webMap) {
    return systemData.webMap;
  }

  const embeddedSystemData = parseJsonObject(systemData?.json);
  return embeddedSystemData?.webMap || null;
};

const normalizeWebMapCanvas = (webMap) => {
  const parsedWebMap = typeof webMap === "string" ? parseJsonObject(webMap) : webMap;
  const canvas = parsedWebMap?.canvas || {};

  return {
    width: normalizeInteger(
      canvas.width ?? parsedWebMap?.width,
      CUSTOM_LAYOUT_WIDTH,
      MIN_CUSTOM_LAYOUT_WIDTH,
      MAX_CUSTOM_LAYOUT_WIDTH,
    ),
    height: normalizeInteger(
      canvas.height ?? parsedWebMap?.height,
      CUSTOM_LAYOUT_HEIGHT,
      MIN_CUSTOM_LAYOUT_HEIGHT,
      MAX_CUSTOM_LAYOUT_HEIGHT,
    ),
  };
};

const getLegacyOverride = (layoutOverrides, environmentId) => {
  const override = layoutOverrides?.[environmentId];
  return override && typeof override === "object" ? override : null;
};

const EnvironmentMap = ({
  systemData,
  currentEnvironmentId,
  changeEnvironment,
  layoutOverrides,
  environmentConditionValues = {},
  className = "",
  containerStyle = null,
}) => {
  const [envList, setEnvList] = useState([]);
  const [positions, setPositions] = useState({});
  const [layoutDimensions, setLayoutDimensions] = useState({
    width: 400,
    height: 400,
  });
  const [isCustomLayout, setIsCustomLayout] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lockedNodePrompt, setLockedNodePrompt] = useState(null);
  const environmentAvailabilityById = useMemo(
    () =>
      getSystemEnvironmentAvailabilityById(
        systemData,
        environmentConditionValues,
      ),
    [environmentConditionValues, systemData],
  );

  // Support both the embedded `json` payload and direct `environments` payloads.
  useEffect(() => {
    setEnvList(getSystemEnvironments(systemData));
  }, [systemData]);

  useEffect(() => {
    if (!lockedNodePrompt || lockedNodePrompt.source !== "press") {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setLockedNodePrompt(null);
    }, LOCKED_NODE_PROMPT_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [lockedNodePrompt]);

  useEffect(() => {
    setLockedNodePrompt(null);
  }, [currentEnvironmentId, environmentConditionValues]);

  const showLockedNodePrompt = useCallback((environmentId, nodeRect, source) => {
    const promptPosition = getLockedPromptPosition(nodeRect);
    setLockedNodePrompt({
      environmentId,
      ...promptPosition,
      requestedAt: Date.now(),
      source,
    });
  }, []);

  const hideLockedNodePrompt = useCallback((environmentId) => {
    setLockedNodePrompt((currentPrompt) =>
      currentPrompt?.environmentId === environmentId ? null : currentPrompt,
    );
  }, []);

  // Compute layout positions and overall container size from envList.
  useEffect(() => {
    if (!envList.length) {
      setPositions({});
      setLayoutDimensions({
        width: 400,
        height: 400,
      });
      setIsCustomLayout(false);
      return;
    }

    const webMap = getSystemWebMap(systemData);
    const customNodes = normalizeWebMapNodes(webMap);
    const customNodeIds = new Set(Object.keys(customNodes));
    const hasCustomLayout = envList.some((env) => customNodeIds.has(env.id));

    if (hasCustomLayout) {
      const customLayoutDimensions = normalizeWebMapCanvas(webMap);
      const fallbackPositions = {};
      envList.forEach((env, index) => {
        fallbackPositions[env.id] = {
          x:
            ((index + 1) * customLayoutDimensions.width) /
            (envList.length + 1),
          y: customLayoutDimensions.height / 2,
        };
      });

      const customPositions = envList.reduce((nextPositions, env) => {
        const customNode = customNodes[env.id];
        nextPositions[env.id] = customNode
          ? {
              x: customNode.x * customLayoutDimensions.width,
              y: customNode.y * customLayoutDimensions.height,
            }
          : fallbackPositions[env.id];

        return nextPositions;
      }, {});

      setPositions(customPositions);
      setLayoutDimensions(customLayoutDimensions);
      setIsCustomLayout(true);
      return;
    }

    setIsCustomLayout(false);

    // Group environments by magnification (default to 1)
    const groups = {};
    envList.forEach((env) => {
      const mag = env.magnification || 1;
      if (!groups[mag]) groups[mag] = [];
      groups[mag].push(env);
    });
    // Sort groups in ascending order so that lower magnification items are at the bottom.
    const sortedMags = Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b);

    // Define layout constants.
    const minHorizontalSpacing = 40; // minimum distance between nodes horizontally
    const verticalSpacing = 65; // vertical space between levels
    const horizontalMargin = 10;
    const topMargin = 46;
    const bottomMargin = 36;
    const maxOverrideRow = envList.reduce((maxRow, env) => {
      const row = Number(getLegacyOverride(layoutOverrides, env.id)?.row);
      return Number.isFinite(row) && row >= 0 ? Math.max(maxRow, row) : maxRow;
    }, -1);
    const numLevels = Math.max(sortedMags.length, maxOverrideRow + 1);

    // Dynamically compute branchWidth: maximum group width among all levels.
    let branchWidth = 0;
    sortedMags.forEach((mag) => {
      const groupLength = groups[mag].length;
      const groupWidth = (groupLength + 1) * minHorizontalSpacing;
      if (groupWidth > branchWidth) branchWidth = groupWidth;
    });

    // Compute positions for each node.
    // For level 0 (lowest magnification), we place the nodes at the bottom.
    const newPositions = {};
    sortedMags.forEach((mag, levelIndex) => {
      // Calculate y so that level 0 is at the bottom:
      // y = verticalSpacing * (numLevels - 1 - levelIndex) + margin.
      const y = verticalSpacing * (numLevels - 1 - levelIndex) + topMargin;
      const group = groups[mag];
      // Use the computed branchWidth to evenly distribute nodes in this group.
      const horizontalSpacing = branchWidth / (group.length + 1);
      group.forEach((env, index) => {
        const override = getLegacyOverride(layoutOverrides, env.id);
        const overrideCol = Number(override?.col);
        const overrideRow = Number(override?.row);
        const baseX = (index + 1) * horizontalSpacing + horizontalMargin;
        const baseY = y;
        const x =
          Number.isFinite(overrideCol)
            ? branchWidth / 2 +
              horizontalMargin +
              overrideCol * minHorizontalSpacing * 1.35
            : baseX;
        const nextY =
          Number.isFinite(overrideRow) && overrideRow >= 0
            ? verticalSpacing * (numLevels - 1 - overrideRow) + topMargin
            : baseY;
        newPositions[env.id] = { x, y: nextY };
      });
    });
    setPositions(newPositions);

    // Compute overall container dimensions.
    const newWidth = branchWidth + 2 * horizontalMargin;
    const newHeight =
      verticalSpacing * (numLevels - 1) + topMargin + bottomMargin;
    setLayoutDimensions({ width: newWidth, height: newHeight });
  }, [envList, layoutOverrides, systemData]);

  useEffect(() => {
    setIsExpanded(false);
  }, [currentEnvironmentId]);

  const currentEnvironment = useMemo(
    () =>
      envList.find((env) => getEnvironmentId(env) === currentEnvironmentId) ||
      envList[0] ||
      null,
    [currentEnvironmentId, envList],
  );
  const effectiveCurrentEnvironmentId = currentEnvironment
    ? getEnvironmentId(currentEnvironment)
    : currentEnvironmentId;

  const effectiveLayoutDimensions = isExpanded
    ? layoutDimensions
    : { width: 360, height: COLLAPSED_MAP_HEIGHT };

  const renderConnectors = () => {
    if (!envList.length) return null;
    const connectors = envList.flatMap((env) => {
      const elements = getEnvironmentElements(env);
      if (!elements.length) return [];
      return elements.map((element, i) => {
        const targetEnvironmentId = getTargetEnvironmentId(element);
        if (!targetEnvironmentId) return null;
        const source = positions[env.id];
        const target = positions[targetEnvironmentId];
        if (!source || !target) return null;
        const isConnectorLocked =
          environmentAvailabilityById[env.id] === false ||
          environmentAvailabilityById[targetEnvironmentId] === false;

        // Compute vertical difference.
        const dy = target.y - source.y;

        // Define key points:
        const S = { ...source }; // source center
        const T = { ...target }; // target center

        // P: 1/3 of the vertical distance from source (keeps source x)
        const P = { x: S.x, y: S.y + dy / 3 };
        // Q: 2/3 of the vertical distance, with x forced to target.x
        const Q = { x: T.x, y: S.y + (2 * dy) / 3 };

        // Choose an offset for the curve
        const offset = (Q.y - P.y) / 1.5;

        // Build the path:
        const pathD = `
          M ${S.x} ${S.y}
          L ${P.x} ${P.y}
          C ${P.x} ${P.y + offset}, ${Q.x} ${Q.y - offset}, ${Q.x} ${Q.y}
          L ${T.x} ${T.y}
        `;

        return (
          <path
            key={`${env.id}-${targetEnvironmentId}-${i}`}
            className={`map-connector ${
              isConnectorLocked ? "map-connector-locked" : ""
            }`}
            d={pathD}
            fill="none"
            strokeWidth="1"
          />
        );
      });
    });
    return connectors.filter((item) => item !== null);
  };

  // Render nodes as spheres with labels underneath.
  const renderNodes = () => {
    return envList.map((env) => {
      const envId = getEnvironmentId(env);
      const isCurrent = effectiveCurrentEnvironmentId === envId;
      const isVisible = isExpanded || isCurrent;
      const isLocked = environmentAvailabilityById[envId] === false && !isCurrent;
      const pos = isExpanded
        ? positions[envId] || { x: 0, y: 0 }
        : { x: CURRENT_NODE_DIAMETER / 2 + 4, y: COLLAPSED_MAP_HEIGHT / 2 };
      return (
        <div
          key={envId}
          className={`environment-node ${
            isCurrent ? "current-node" : ""
          } ${isLocked ? "locked-node" : ""} ${
            isVisible ? "" : "map-node-hidden"
          }`}
          aria-hidden={!isVisible}
          style={{ left: pos.x, top: pos.y }}
        >
          <NodeSphere
            environment={env}
            thumbnailCacheKey={`${systemData?.id ?? "system"}:${envId}`}
            currentEnvironmentId={effectiveCurrentEnvironmentId}
            isMapExpanded={isExpanded}
            isLocked={isLocked}
            onNodeClick={(environmentId, isCurrent) => {
              if (isCurrent) {
                setIsExpanded((expanded) => !expanded);
                return;
              }

              setIsExpanded(false);
              changeEnvironment && changeEnvironment(environmentId);
            }}
            onLockedNodePrompt={showLockedNodePrompt}
            onLockedNodePromptClear={hideLockedNodePrompt}
            labelPortal={isExpanded && isCustomLayout}
          />
          {/* Uncomment and customize the label if desired:
          <div className="node-label">
            {env.environmentName}
            {env.magnification ? ` (${formatMagnification(env.magnification)})` : ""}
          </div>
          */}
        </div>
      );
    });
  };

  return (
    <div
      className={[
        "map-container",
        isExpanded ? "expanded-map-container" : "collapsed-map-container",
        isCustomLayout ? "custom-map-container" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        width: effectiveLayoutDimensions.width,
        height: effectiveLayoutDimensions.height,
        ...containerStyle,
      }}
    >
      <svg
        aria-hidden={!isExpanded}
        className="connectors"
        width={layoutDimensions.width}
        height={layoutDimensions.height}
        viewBox={`0 0 ${layoutDimensions.width} ${layoutDimensions.height}`}
      >
        {renderConnectors()}
      </svg>
      {renderNodes()}
      {lockedNodePrompt && <LockedNodePrompt prompt={lockedNodePrompt} />}
    </div>
  );
};

const NodeSphere = ({
  environment,
  thumbnailCacheKey,
  onNodeClick,
  currentEnvironmentId,
  isMapExpanded = false,
  labelPortal = false,
  isLocked = false,
  onLockedNodePrompt,
  onLockedNodePromptClear,
}) => {
  const environmentId = getEnvironmentId(environment);
  const environmentName =
    environment.environmentName || environment.displayName || environment.name;
  const isCurrent = currentEnvironmentId === environmentId;
  const [labelShowing, setLabelShowing] = useState(false);
  const [labelPosition, setLabelPosition] = useState({x:0,y:0})
  const sphereRef = useRef(null);
  const thumbnailUrl = useCachedThumbnail(
    getEnvironmentId(environment),
    environment.thumbnailUrl,
    thumbnailCacheKey,
  );

  const getLabelPosition = useCallback((nodeElement, labelAnchorDiameter) => {
    if (!nodeElement || !nodeElement.parentNode) return null;

    const sphereRect = nodeElement.getBoundingClientRect();
    if (labelPortal) {
      return {
        x: sphereRect.left + sphereRect.width / 2 + labelAnchorDiameter / 2,
        y: sphereRect.top + sphereRect.height / 2,
      };
    }

    const parentRect = nodeElement.parentNode.getBoundingClientRect();
    const centerX = sphereRect.left - parentRect.left + sphereRect.width / 2;
    const centerY = sphereRect.top - parentRect.top + sphereRect.height / 2;

    return { x: centerX + labelAnchorDiameter / 2, y: centerY };
  }, [labelPortal]);

  const updateLabelPosition = useCallback(
    (labelAnchorDiameter, nodeElement = sphereRef.current) => {
      const nextLabelPosition = getLabelPosition(nodeElement, labelAnchorDiameter);
      if (nextLabelPosition) {
        setLabelPosition(nextLabelPosition);
      }
    },
    [getLabelPosition]
  );

  useEffect(() => {
    if (isCurrent) {
      // Measure immediately, then after transition settles to avoid
      // first-frame overlap when auto-selecting during zoom state changes.
      updateLabelPosition(50);
      setLabelShowing(true);

      const rafId = requestAnimationFrame(() => {
        updateLabelPosition(50);
      });
      const timeoutId = setTimeout(() => {
        updateLabelPosition(50);
      }, 130);

      const sphereElement = sphereRef.current;
      const handleTransitionEnd = (event) => {
        if (event.propertyName === "width" || event.propertyName === "height") {
          updateLabelPosition(50);
        }
      };
      sphereElement?.addEventListener("transitionend", handleTransitionEnd);

      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId);
        sphereElement?.removeEventListener("transitionend", handleTransitionEnd);
      };
    }

    else {
      setLabelShowing(false);
    }
  }, [isCurrent, updateLabelPosition]);

  useEffect(() => {
    if (!isCurrent || !labelPortal) {
      return undefined;
    }

    const startedAt = Date.now();
    let rafId = null;
    let timeoutId = null;

    const trackLabelPosition = () => {
      updateLabelPosition(CURRENT_NODE_DIAMETER);

      if (Date.now() - startedAt < 420) {
        rafId = requestAnimationFrame(trackLabelPosition);
      }
    };

    trackLabelPosition();
    timeoutId = setTimeout(() => {
      updateLabelPosition(CURRENT_NODE_DIAMETER);
    }, 440);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isCurrent, isMapExpanded, labelPortal, updateLabelPosition]);

  const handleClicked = (event) => {
    event.stopPropagation();
    if (isLocked) {
      onLockedNodePrompt &&
        onLockedNodePrompt(
          environmentId,
          event.currentTarget?.getBoundingClientRect(),
          "press",
        );
      return;
    }

    onNodeClick && onNodeClick(environmentId, isCurrent);
  }

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleClicked(event);
  };

  const showLabel = (event) => {
    const labelAnchorDiameter = isCurrent ? 50 : 40;
    updateLabelPosition(labelAnchorDiameter, event.currentTarget);
    setLabelShowing(true);
    if (isLocked) {
      onLockedNodePrompt &&
        onLockedNodePrompt(
          environmentId,
          event.currentTarget?.getBoundingClientRect(),
          "hover",
        );
    }
  };

  const getMagnification = () => {
    return environment.magnification < 1 ? `${environment.worldUnit.value}${environment.worldUnit.units}`  : `${environment.magnification.toLocaleString()}x`;
  }



  const hideLabel = () => {
    if(!isCurrent) setLabelShowing(false);
    if (isLocked) {
      onLockedNodePromptClear && onLockedNodePromptClear(environmentId);
    }
  }

  return (
    <div
      style={{ position: "relative" }}
      onMouseMove={(e) => e.stopPropagation()}
    >
      <div
        className={`node-sphere ${isCurrent ? "current" : ""} ${
          isLocked ? "locked" : ""
        }`}
        ref={sphereRef}
        role="button"
        tabIndex={isLocked ? -1 : 0}
        aria-disabled={isLocked}
        aria-label={
          isLocked
            ? `${environmentName || "Environment"} unavailable. Explore to unlock.`
            : environmentName || "Environment"
        }
        onClick={handleClicked}
        onKeyDown={handleKeyDown}
        onMouseEnter={showLabel}
        onMouseLeave={hideLabel}
        style={{
          backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {isLocked && (
          <span
            className="node-locked-badge"
            aria-hidden="true"
            title="Explore to unlock"
          >
            <VisibilityGatedIcon />
          </span>
        )}
      </div>
      {labelShowing && (
        <Label
          labelPosition={labelPosition}

          displayName={environmentName}
          subtitle={getMagnification()}
          alignment="right"
          offsetLeft={8}
          portal={labelPortal}
          portalZIndex="var(--environment-map-popover-z-index, 1101)"
        />
      )}
    </div>
  );
}

const VisibilityGatedIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    <path d="M3.6 3.1 20.9 20.4" />
    <path d="M10.2 5.3c.58-.13 1.18-.2 1.8-.2 5.2 0 8.7 5.1 9.4 6.3-.3.5-1.1 1.7-2.3 2.8" />
    <path d="M15.2 15.4a4.1 4.1 0 0 1-5.5-5.5" />
    <path d="M6.7 7.4c-2.2 1.4-3.6 3.5-4.1 4.2.7 1.2 4.2 6.3 9.4 6.3 1.5 0 2.8-.4 4-1.1" />
  </svg>
);

const LockedNodePrompt = ({ prompt }) => {
  if (
    typeof document === "undefined" ||
    typeof prompt?.x !== "number" ||
    typeof prompt?.y !== "number"
  ) {
    return null;
  }

  return createPortal(
    <div
      className={`node-locked-message node-locked-message-${prompt.placement}`}
      role="status"
      style={{ left: prompt.x, top: prompt.y }}
    >
      Explore to unlock
    </div>,
    document.body,
  );
};



export default EnvironmentMap;
